import { useState } from "react";
import { CheckCircle2, Loader2, Plus } from "lucide-react";
import {
  useAddDefect,
  useAddPhoto,
  useProtocolDefects,
  useProtocolPhotos,
  useRemovePhoto,
} from "@/hooks/useProtocol";
import { PhotoGrid } from "@/components/protocol/PhotoGrid";
import { ReportDefectSheet } from "@/components/protocol/ReportDefectSheet";
import {
  COMMON_AREAS,
  getMissingCommonArea,
  MIN_COMMON_AREA_PHOTOS,
  type CommonArea,
} from "@/lib/protocol";
import { formatMsg, type ProtocolCopy } from "@/lib/protocol-copy";

type AreaId = CommonArea;
const TOTAL_CAP = 4;

export const AREA_KEYS: Record<AreaId, keyof ProtocolCopy> = {
  kitchen: "commonAreaKitchen",
  bath: "commonAreaBath",
  hallway: "commonAreaHallway",
  other: "commonAreaOther",
};

export function CommonAreasStep({
  protocolId,
  t,
}: {
  protocolId: string;
  t: ProtocolCopy;
}) {
  const [active, setActive] = useState<AreaId>("kitchen");
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data: photos = [], isLoading } = useProtocolPhotos(protocolId);
  const { data: defects = [] } = useProtocolDefects(protocolId);
  const addPhoto = useAddPhoto(protocolId);
  const removePhoto = useRemovePhoto(protocolId);
  const addDefect = useAddDefect(protocolId);

  const photosForArea = (a: AreaId) => photos.filter((p) => p.category === a);
  const areaDone = (a: AreaId) =>
    photosForArea(a).length >= MIN_COMMON_AREA_PHOTOS;

  const currentPhotos = photosForArea(active);
  const currentLabel = t[AREA_KEYS[active]] as string;

  return (
    <div>
      <h2 className="text-[26px] font-bold leading-tight text-neutral-900 md:text-3xl">
        {t.commonTitle}
      </h2>
      <p className="mt-1 text-sm text-neutral-500 md:max-w-2xl">
        {formatMsg(t.commonSubtitle, { min: MIN_COMMON_AREA_PHOTOS })}
      </p>

      <div className="mt-5 flex flex-wrap gap-2 md:mt-8 md:gap-3">
        {COMMON_AREAS.map((a) => {
          const selected = active === a.id;
          const done = areaDone(a.id);
          const label = t[AREA_KEYS[a.id]] as string;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setActive(a.id)}
              className={[
                "flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition",
                selected
                  ? "bg-neutral-900 text-white"
                  : "bg-neutral-100 text-neutral-700",
              ].join(" ")}
            >
              {label}
              <span
                className={[
                  "flex h-5 w-5 items-center justify-center rounded-full",
                  done
                    ? "bg-emerald-500 text-white"
                    : selected
                      ? "bg-white/20 text-white"
                      : "bg-white text-neutral-400",
                ].join(" ")}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-900">
          {formatMsg(t.photosCount, {
            n: currentPhotos.length,
            max: TOTAL_CAP,
          })}
        </span>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex items-center gap-1 text-sm font-medium text-emerald-600"
        >
          <Plus className="h-4 w-4" />
          {t.reportDefect}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="mt-3">
          <PhotoGrid
            photos={currentPhotos}
            category={active}
            onAdd={(file, ordering) =>
              addPhoto.mutateAsync({ file, category: active, ordering })
            }
            onRemove={(photo) => removePhoto.mutateAsync(photo)}
            uploading={addPhoto.isPending}
            max={TOTAL_CAP}
          />
        </div>
      )}

      {defects.length ? (
        <div className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            {t.reportedDefects}
          </div>
          <ul className="mt-2 space-y-2">
            {defects.map((d) => (
              <li
                key={d.id}
                className="rounded-xl border border-neutral-200 p-3 text-sm"
              >
                <div className="font-medium text-neutral-900">
                  {(t[AREA_KEYS[d.area as AreaId]] as string) ?? d.area} ·{" "}
                  {d.severity === "low"
                    ? t.severityLow
                    : d.severity === "medium"
                      ? t.severityMedium
                      : t.severityHigh}
                </div>
                <div className="text-neutral-600">{d.description}</div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <ReportDefectSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        subtitle={currentLabel}
        onSave={({ description, severity, file }) =>
          addDefect.mutateAsync({ area: active, description, severity, file })
        }
      />
    </div>
  );
}

export function isCommonAreasComplete(photos: { category: string }[]): boolean {
  return getMissingCommonArea(photos) === null;
}
